import React, { lazy, Suspense } from 'react';
import { VendorMicrosoftCallback } from './portal/VendorMicrosoftLogin';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import {
  AuthProvider,
  useAuth,
  ProtectedRoute,
  type AppEntitlementManifest,
} from '@es-tools/shared';
import { isDataExplorerAskEnabled, isDataExplorerEnabled } from './data-explorer/featureFlag';
import { redirectedDataPath } from './data-explorer/sections';
import { metricInputsRoute } from './data-explorer/inputs';
import { dataIssueRoutes } from './data-explorer/issues';
import { contextRedirectTarget } from './data-explorer/contextRedirect';
import { SAVED_CHART_ROUTES } from './data-explorer/savedChartsView';
import { dashboardViewerPageAllowed, isExternalDashboardViewer, dashboardDataScopeKey } from './data-explorer/dashboardViewer';
import { simpleDataRedirect } from './data-explorer/dataExperience';
import { setProtectedDataScope } from './data-explorer/protectedDataCache';
import { LONG_DATA_JOB_ROUTE } from './data-explorer/longDataJobsClient';

const Login = lazy(() => import('./pages/Login'));
const VendorWorkspace = lazy(() => import('./portal/VendorWorkspace'));
const BrandHome = lazy(() => import('./pages/BrandHome'));
const ReportsPage = lazy(() => import('./reports/ReportsPage'));
const AppPlaceholder = lazy(() => import('./pages/AppPlaceholder'));
const HolyShipped = lazy(() => import('./pages/HolyShipped'));
const AutomationLibrary = lazy(() => import('./pages/AutomationLibrary'));
const DataLanding = lazy(() => import('./data-explorer/pages/DataLanding'));
const TablePage = lazy(() => import('./data-explorer/pages/TablePage'));
const MetricPage = lazy(() => import('./data-explorer/pages/MetricPage'));
const QueryPage = lazy(() => import('./data-explorer/pages/QueryPage'));
const AskPage = lazy(() => import('./data-explorer/pages/AskPage'));
const SavedPage = lazy(() => import('./data-explorer/pages/SavedPage'));
const ChartsPage = lazy(() => import('./data-explorer/pages/ChartsPage'));
const ChartPage = lazy(() => import('./data-explorer/pages/ChartPage'));
const JobPage = lazy(() => import('./data-explorer/pages/JobPage'));
const BrowsePage = lazy(() => import('./data-explorer/pages/BrowsePage'));
const SearchPage = lazy(() => import('./data-explorer/pages/SearchPage'));
const SourcesPage = lazy(() => import('./data-explorer/pages/SourcesPage').then((module) => ({ default: module.SourcesPage })));
const SourcePage = lazy(() => import('./data-explorer/pages/SourcesPage').then((module) => ({ default: module.SourcePage })));
const DatasetsPage = lazy(() => import('./data-explorer/pages/DatasetsPage').then((module) => ({ default: module.DatasetsPage })));
const DatasetPage = lazy(() => import('./data-explorer/pages/DatasetsPage').then((module) => ({ default: module.DatasetPage })));
const MetricsPage = lazy(() => import('./data-explorer/pages/MetricsPage'));
const LineagePage = lazy(() => import('./data-explorer/pages/LineagePage'));
const GuidePage = lazy(() => import('./data-explorer/pages/GuidePage'));
const AccessPage = lazy(() => import('./data-explorer/pages/AccessPage'));
const ContractsPage = lazy(() => import('./data-explorer/pages/ContractsPage').then((module) => ({ default: module.ContractsPage })));
const ConnectPage = lazy(() => import('./data-explorer/pages/ConnectPage'));
const SkillsGalleryPage = lazy(() => import('./data-explorer/skills/SkillsGalleryPage'));
const SkillDetailPage = lazy(() => import('./data-explorer/skills/SkillDetailPage'));
const SkillEditorPage = lazy(() => import('./data-explorer/skills/SkillEditorPage'));
const SkillReviewPage = lazy(() => import('./data-explorer/skills/SkillReviewPage'));
const SkillsGuidePage = lazy(() => import('./data-explorer/skills/SkillsGuidePage'));
const YouPage = lazy(() => import('./data-explorer/pages/YouPage'));
const FinanceAssumptionsPage = lazy(() => import('./data-explorer/inputs/FinanceAssumptionsPage'));
const FinanceCostSavingsPage = lazy(() => import('./data-explorer/inputs/FinanceCostSavingsPage'));
const DashboardsPage = lazy(() => import('./data-explorer/pages/DashboardsPage'));
const ContractPage = lazy(() => import('./data-explorer/pages/ContractsPage').then((module) => ({ default: module.ContractPage })));

/** Old Data paths (for example `/data/browse`) keep working: same query, new path. */
const DataPathRedirect: React.FC = () => {
  const location = useLocation();
  return <Navigate to={redirectedDataPath(location.pathname, location.search) ?? '/data'} replace />;
};

/**
 * Context OS (`/context`) was folded into Data on 2026-10-01. `/context`, any
 * `/context/*` path and the old in-page anchors redirect to the matching Data
 * page (Use with Claude, Skills or You); query strings carry over. The
 * redirect runs before sign-in so an old link survives the login round trip.
 */
const ContextRedirect: React.FC = () => {
  const location = useLocation();
  return <Navigate to={contextRedirectTarget(location.pathname, location.search, location.hash)} replace />;
};

/**
 * The pages folded in from Context OS. They do not follow the Data Explorer
 * flag: `/context` was always on, so its redirect targets must always exist.
 */
const DATA_CONTEXT_ROUTES: Array<[string, React.ComponentType]> = [
  ['/data/connect', ConnectPage],
  ['/data/skills', SkillsGalleryPage],
  ['/data/skills/new', SkillEditorPage],
  ['/data/skills/drafts/:draftId', SkillEditorPage],
  ['/data/skills/review', SkillReviewPage],
  ['/data/skills/guide', SkillsGuidePage],
  ['/data/skills/:skillId', SkillDetailPage],
  ['/data/you', YouPage],
];

const DATA_EXPLORER_ROUTES: Array<[string, React.ComponentType]> = [
  ['/data', DataLanding],
  ['/data/tables', BrowsePage],
  ['/data/browse', DataPathRedirect],
  ['/data/search', SearchPage],
  ['/data/sources', SourcesPage],
  ['/data/sources/:sourceId', SourcePage],
  ['/data/datasets', DatasetsPage],
  ['/data/datasets/:datasetId', DatasetPage],
  ['/data/tables/:database/:table', TablePage],
  ['/data/dashboards', DashboardsPage],
  ['/data/dashboards/:department', DashboardsPage],
  ['/data/metrics', MetricsPage],
  ['/data/metrics/:metricId', MetricPage],
  ['/data/lineage', LineagePage],
  ['/data/guide', GuidePage],
  ['/data/access', AccessPage],
  ['/data/contracts', ContractsPage],
  ['/data/contracts/:contractId', ContractPage],
  metricInputsRoute,
  ['/data/inputs/finance/profit-assumptions', FinanceAssumptionsPage],
  ['/data/inputs/finance/cost-savings', FinanceCostSavingsPage],
  ...dataIssueRoutes,
  ['/data/query', QueryPage],
  ['/data/saved', SavedPage],
  [SAVED_CHART_ROUTES.library, ChartsPage],
  [SAVED_CHART_ROUTES.detail, ChartPage],
  [LONG_DATA_JOB_ROUTE, JobPage],
  // P-06: the Ask surface has its own default-off flag; the route is absent
  // (and the catch-all redirects home) until the flag is enabled.
  ...(isDataExplorerAskEnabled()
    ? ([['/data/ask', AskPage]] as Array<[string, React.ComponentType]>)
    : []),
];

const DEFAULT_ALLOWED_EMAIL_DOMAINS = [
  'hirehoratio.co',
  'mengotomars.com',
  'hollowsocks.com',
  'paradigmcapital.xyz',
];

const parseAllowedDomains = (rawValue?: string): string[] => {
  const source = rawValue ?? DEFAULT_ALLOWED_EMAIL_DOMAINS.join(',');
  return source
    .split(',')
    .map((domain) => domain.trim().toLowerCase())
    .filter((domain) => domain.length > 0);
};

const allowedDomains = parseAllowedDomains(import.meta.env.VITE_ALLOWED_EMAIL_DOMAINS);

const DEV_APP_ENTITLEMENTS: AppEntitlementManifest = {
  groups: [
    'mm-app-all-employees@mengotomars.com',
    'mm-app-data@mengotomars.com',
    'mm-app-platform-admins@mengotomars.com',
    'mm-app-access-admins@mengotomars.com',
  ],
  roles: {
    reviews: 'viewer',
    'customer-interviews': 'viewer',
    'creative-analytics': 'viewer',
    'landing-page-generator': 'viewer',
    'quiz-admin': 'viewer',
    'creative-library': 'viewer',
    'video-briefs': 'viewer',
    'youtube-bulk-uploader': 'viewer',
    'video-image-generation': 'viewer',
    'agency-content': 'viewer',
    'review-moderator': 'viewer',
    'partnership-manager': 'viewer',
    'mission-control': 'viewer',
    'operations-hub': 'viewer',
    'ap-workbench': 'viewer',
    'media-buying': 'viewer',
    'subscription-intelligence': 'viewer',
    'finance-command-center': 'viewer',
    'brand-defense': 'viewer',
    'meta-creative-launcher': 'operator',
    'company-broadcasts': 'operator',
    'trybe-intelligence': 'operator',
    'brain-dashboard': 'operator',
    openmetadata: 'viewer',
    'dashboard-viewer': 'admin',
    'data-platform': 'operator',
    'automation-monitor': 'admin',
    'ai-center': 'admin',
  },
  source: 'explicit-local-fixture',
  revision: 'local-fixture',
};

type AppProps = {
  authApiBaseUrl: string;
};

function ProtectedDataBoundary({children}:{children:React.ReactNode}) {
  const {user}=useAuth();
  // Clear synchronously before a new principal's readers render; key also drops
  // already-mounted local state and aborts old dashboard/support requests.
  const scope=setProtectedDataScope(dashboardDataScopeKey(user));
  return <React.Fragment key={scope}>{children}</React.Fragment>;
}

function DataViewerRoute({children}: {children: React.ReactNode}) {
  const {user}=useAuth(); const location=useLocation();
  const simpleRedirect = simpleDataRedirect(user, location.pathname);
  if (simpleRedirect) return <Navigate to={simpleRedirect} replace />;
  if(user?.identityType==='vendor'&&(!isExternalDashboardViewer(user)||!dashboardViewerPageAllowed(location.pathname)))
    return <Navigate to={isExternalDashboardViewer(user)?'/data/dashboards':'/vendors/workspace'} replace />;
  return <>{children}</>;
}

function App({ authApiBaseUrl }: AppProps) {
  const devBypassEnabled = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true';

  return (
    <AuthProvider
      allowDevBypass={devBypassEnabled}
      allowedDomains={allowedDomains}
      authApiBaseUrl={authApiBaseUrl}
      devAppEntitlements={devBypassEnabled ? DEV_APP_ENTITLEMENTS : undefined}
    >
      <ProtectedDataBoundary><Router>
        <Suspense fallback={<div className="route-loading" role="status"><span />Loading workspace…</div>}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/vendors/microsoft-callback" element={<VendorMicrosoftCallback />} />
            <Route path="/vendors/workspace" element={<ProtectedRoute><VendorWorkspace /></ProtectedRoute>} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <BrandHome />
                </ProtectedRoute>
              }
            />
            <Route
              path="/holy-shipped"
              element={
                <ProtectedRoute>
                  <HolyShipped />
                </ProtectedRoute>
              }
            />
            <Route
              path="/holy-shipped/week/:weekNumber"
              element={
                <ProtectedRoute>
                  <HolyShipped />
                </ProtectedRoute>
              }
            />
            <Route
              path="/automation-library"
              element={
                <ProtectedRoute>
                  <AutomationLibrary />
                </ProtectedRoute>
              }
            />
            <Route
              path="/data-platform"
              element={<Navigate to="/data" replace />}
            />
            <Route path="/context" element={<ContextRedirect />} />
            <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
            <Route path="/reports/:reportId" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
            <Route path="/context/*" element={<ContextRedirect />} />
            {DATA_CONTEXT_ROUTES.map(([path, Page]) => (
              <Route
                key={path}
                path={path}
                element={
                  <ProtectedRoute
                    requiredAppId="data-platform"
                    defaultRole="none"
                    enforcement="enforced"
                  >
                    <Page />
                  </ProtectedRoute>
                }
              />
            ))}
            {isDataExplorerEnabled()
              ? DATA_EXPLORER_ROUTES.map(([path, Page]) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <ProtectedRoute
                      requiredAppId="data-platform"
                      defaultRole="none"
                      enforcement="enforced"
                    >
                      <DataViewerRoute><Page /></DataViewerRoute>
                    </ProtectedRoute>
                  }
                />
              ))
              : null}
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <AppPlaceholder appName="Analytics Dashboard" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory"
              element={
                <ProtectedRoute>
                  <AppPlaceholder appName="Inventory Manager" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/campaigns"
              element={
                <ProtectedRoute>
                  <AppPlaceholder appName="Marketing Campaigns" />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Router></ProtectedDataBoundary>
    </AuthProvider>
  );
}

export default App;
